/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { SmartSearchService } from "@tivocorp/data-domain-lib";
import type {
  TSmartSearchCarouselParams,
  TSmartSearchScreenParams,
  TSmartSearchGetHistoryParams,
  TSmartSearchSaveHistoryParams,
  TSmartSearchDeleteHistoryParams
} from "@tivocorp/data-domain-lib";
import type { Carousel, SearchScreen } from "client-code/smart-search";

import type { TNetworkOptions } from "../types";
import { Log } from "../logging";
import { ApplicationFeatureArea } from "../enums";
import { TIVO_SERVICES } from "../../config";
import { BaseInteractor } from "./base";

/**
 * Facade class that wraps SmartSearchService behind a single entry
 * point for smartSearch screen retrieval and search-history management.
 *
 * Service instances are **not** stored as class members. A fresh
 * instance is created on every method call so the interactor holds
 * no mutable state. TServicesParams is built internally; callers
 * do not supply it.
 *
 * The response contract is the generated, grouped-carousel
 * `SearchScreen` (client-code/smart-search): the server groups
 * results into `carousels[]`, labels each with a `caption`, and supplies
 * the tile `template` and per-carousel `resolvedCount`. Grouping is no
 * longer derived client-side.
 */
export class SmartSearchScreenInteractor extends BaseInteractor {
  private static readonly TAG: string = "SmartSearchScreenInteractor";

  private async createSmartSearchService(): Promise<SmartSearchService> {
    return new SmartSearchService(
      await this.buildServicesParams(
        TIVO_SERVICES.SmartSearch,
        ApplicationFeatureArea.SMART_SEARCH
      )
    );
  }

  /**
   * Retrieves the grouped smartSearch screen (carousels of feed items)
   * for the given search term. `params.pageCursor` fetches the next page
   * of carousels when present in a prior response.
   */
  public async fetchSmartSearchScreen(
    params: TSmartSearchScreenParams,
    options?: TNetworkOptions
  ): Promise<SearchScreen> {
    return this.executeGuarded(
      "fetchSmartSearchScreen",
      async (guardedSignal: AbortSignal): Promise<SearchScreen> => {
        Log.d(
          SmartSearchScreenInteractor.TAG,
          "fetchSmartSearchScreen() called"
        );
        const service: SmartSearchService =
          await this.createSmartSearchService();
        return service.fetchSmartSearchScreen(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Retrieves the next item page for one Smart Search carousel. The caller
   * supplies the normalized original search term and the carousel cursor;
   * this is independent from SearchScreen's cursor for carousel rows.
   */
  public async fetchSmartSearchCarousel(
    params: TSmartSearchCarouselParams,
    options?: TNetworkOptions
  ): Promise<Carousel> {
    return this.executeGuarded(
      "fetchSmartSearchCarousel",
      async (guardedSignal: AbortSignal): Promise<Carousel> => {
        Log.d(
          SmartSearchScreenInteractor.TAG,
          "fetchSmartSearchCarousel() called"
        );
        const service: SmartSearchService =
          await this.createSmartSearchService();
        return service.fetchSmartSearchCarousel(params, guardedSignal);
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchSearchHistory(
    params: TSmartSearchGetHistoryParams,
    options?: TNetworkOptions
  ): Promise<Array<string>> {
    return this.executeGuarded(
      "fetchSearchHistory",
      async (guardedSignal: AbortSignal): Promise<Array<string>> => {
        Log.d(SmartSearchScreenInteractor.TAG, "fetchSearchHistory() called");
        const service: SmartSearchService =
          await this.createSmartSearchService();
        return service.fetchSearchHistory(params, guardedSignal);
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async createSearchHistory(
    params: TSmartSearchSaveHistoryParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "createSearchHistory",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(SmartSearchScreenInteractor.TAG, "createSearchHistory() called");
        const service: SmartSearchService =
          await this.createSmartSearchService();
        return service.createSearchHistory(params, guardedSignal);
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async deleteSearchHistory(
    params: TSmartSearchDeleteHistoryParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "deleteSearchHistory",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(SmartSearchScreenInteractor.TAG, "deleteSearchHistory() called");
        const service: SmartSearchService =
          await this.createSmartSearchService();
        return service.deleteSearchHistory(params, guardedSignal);
      },
      options
    );
  }
}
